/**
 * 14029 — Shared helpers that decide whether a grid row should display a Risk
 * Summary AND how the column should sort. Used by every grid surface listed in
 * the ticket so all four behave identically.
 *
 * The "rules" for `--` per AC:
 *   - VQQ is not a Diligent template (custom / Lilly-custom etc.)
 *   - VQQ has not reached Complete / Released / Published status
 *   - Risk scoring was not purchased on the VQQ (neither Auto-Score nor SME)
 *   - Diligent admin has not yet published a risk-score snapshot
 *
 * The first three checks are local to the row. The fourth is "no summary in
 * the batch lookup" → cell renders `--` automatically.
 */

/**
 * Grid rows arrive in many shapes (snake_case from C#, camelCase from FE
 * mappers). Reach into the most common spots to find the sponsor_rfi_response
 * primary key.
 */
export function getRowSponsorRfiResponseId(item) {
  if (!item) return null;
  return (
    item.sponsor_rfi_response_id ??
    item.sponsorRfiResponseId ??
    item.SponsorRfiResponseId ??
    item.entity_id ??
    item.entityId ??
    null
  );
}

/** Extracts the list of sponsor_rfi_response_ids visible on a grid page. */
export function extractRiskRowIds(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const id = getRowSponsorRfiResponseId(item);
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/**
 * Returns true when this row CAN ever show a Risk Summary, regardless of
 * whether a published snapshot currently exists. Rows where this returns false
 * always render `--` (cheap pre-filter that avoids unnecessary chip layouts).
 *
 * Per AC: requires a Diligent template AND a Complete-or-later status AND
 * risk-scoring was opted in at request time.
 */
export function isRiskSummaryApplicable(item) {
  if (!item) return false;

  // 1) Diligent template only — custom VQQs never show.
  const isDiligent = isDiligentVqqRow(item);
  if (!isDiligent) return false;

  // 2) Must be at-or-past completion.
  if (!isRowCompleteOrPublished(item)) return false;

  // 3) Risk scoring must have been purchased at request time.
  if (!isRiskScoringPurchasedOnRow(item)) return false;

  return true;
}

/** True when the VQQ row is a Diligent (global) template. */
export function isDiligentVqqRow(item) {
  if (!item) return false;
  if (item.is_global_template === true || item.is_global_template === "true") return true;
  if (item.isGlobalTemplate === true) return true;
  const type = String(item.type ?? item.Type ?? "").toLowerCase();
  if (type.includes("diligent")) return true;
  return false;
}

/** True when the VQQ row has reached at-or-past "Complete" (Complete/Released/Published). */
export function isRowCompleteOrPublished(item) {
  if (!item) return false;
  const status = String(item.status ?? item.Status ?? "").trim().toLowerCase();
  if (!status) return false;
  return (
    status === "complete" ||
    status === "completed" ||
    status === "released" ||
    status === "published"
  );
}

/**
 * True when either add-on was ticked at request time. Backend stamps
 * `enable_auto_score` / `enable_sme_score` on each VQQ row when persisted.
 * Older rows (before 14028) won't carry the field and default false.
 */
export function isRiskScoringPurchasedOnRow(item) {
  if (!item) return false;
  const auto = item.enable_auto_score ?? item.enableAutoScore;
  const sme = item.enable_sme_score ?? item.enableSmeScore;
  return Boolean(auto) || Boolean(sme);
}

/**
 * Lookup helper used inside cell renderers + sort comparators. Returns the
 * matching summary OR null when this row is not entitled to show a summary
 * (applicability gate fails OR no published snapshot in the lookup).
 */
export function getRowRiskSummary(item, summariesByRowId) {
  if (!isRiskSummaryApplicable(item)) return null;
  const id = getRowSponsorRfiResponseId(item);
  if (!id) return null;
  return summariesByRowId?.[id] ?? null;
}

/**
 * Library Requests / My Documents rows are documents: they carry neither the
 * template kind nor the add-on flags, so the row rule above cannot run on them.
 * The summaries endpoint already leaves out every VQQ that never asked for risk
 * scoring, so a VQQ document row shows what came back and an audit row shows none.
 */
export function getLibraryRequestRiskSummary(item, summariesByRowId) {
  if (String(item?.document_type || "").toLowerCase() !== "rfi") return null;
  const id = getRowSponsorRfiResponseId(item);
  if (!id) return null;
  return summariesByRowId?.[id] ?? null;
}

export const RISK_SUMMARY_EXPORT_HEADER = "Risk Summary";

/** Export headers for a viewer who cannot see the Risk Summary column in the grid. */
export function withoutRiskSummaryColumn(headers) {
  return (headers || []).filter((header) => header !== RISK_SUMMARY_EXPORT_HEADER);
}

/**
 * Sort comparator factory for the Risk Summary column. `--` rows always sort
 * to the bottom regardless of asc/desc. Two `--` rows are equal. `getSummary`
 * must be the same lookup the grid's cells render, so the order always matches
 * what is on screen.
 */
export function makeRiskSummarySortComparator(summariesByRowId, direction, getSummary = getRowRiskSummary) {
  const mult = direction === "asc" ? 1 : -1;
  return (a, b) => {
    const aSummary = getSummary(a, summariesByRowId);
    const bSummary = getSummary(b, summariesByRowId);
    const aMissing = !aSummary;
    const bMissing = !bSummary;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    const aScore = Number(aSummary.overall_score) || 0;
    const bScore = Number(bSummary.overall_score) || 0;
    if (aScore === bScore) return 0;
    return aScore < bScore ? -mult : mult;
  };
}
